import { Navigate, Outlet, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import {
  AppShell,
  Button,
  type ShellNavGroup,
  type ShellNavItem,
} from "@teamna/ui/components/index.ts";
import { assistantNavState, ownerNavGroups, ownerSettingsItem } from "@teamna/ui/owner/nav.ts";
import { useLabels } from "@teamna/web-client/hooks/use-labels.ts";
import { useShape } from "@teamna/web-client/hooks/use-shape.ts";
import { forBusiness, totalUnread, useConversations } from "@teamna/web-client/hooks/use-messaging.ts";
import { setActiveBusinessId, useActiveBusiness } from "@teamna/web-client/hooks/use-owner.ts";
import { setActingAs, useActingAs } from "../lib/act-as.ts";
import { RequireStaff, useControlCenterSignOut } from "./staff-layout.tsx";

/**
 * Acting as a business (control-center-split.md, section 4).
 *
 * The Desk owner screens from `@teamna/ui/owner`, at the same `/owner/*` addresses as in Desk, in
 * the Control Center shell. Every request they make goes to `/v1/desk/...` with
 * `X-Act-As-Business`, and the API runs it as the business owner. The banner is on every page
 * while acting; Stop leaves and returns to the businesses list.
 */
export function ActAsLayout() {
  const acting = useActingAs();
  if (!acting) return <Navigate to="/businesses" replace />;
  return (
    <RequireStaff>
      {(staff) => <ActAsShell businessId={acting.businessId} businessName={acting.businessName} staff={staff} />}
    </RequireStaff>
  );
}

export function ActAsBanner({ businessName, onStop }: { businessName: string; onStop: () => void }) {
  return (
    <aside
      aria-label="Acting as a business"
      className="flex items-center justify-between gap-3 border-b border-line-strong bg-late-surface px-5 py-2.5 lg:px-10"
    >
      <p className="min-w-0 text-[14px] font-semibold text-ink">
        Acting as {businessName} as Teamna support
      </p>
      <Button variant="secondary" onClick={onStop}>
        Stop
      </Button>
    </aside>
  );
}

function ActAsShell({
  businessId,
  businessName,
  staff,
}: {
  businessId: string;
  businessName: string;
  staff: { email: string; name: string | null };
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const signOut = useControlCenterSignOut();
  const { business } = useActiveBusiness();
  const { label } = useLabels();
  const conversations = useConversations();

  // The owner screens read the business from the owner store. The API answers only this one
  // business from `/me/businesses`, and this points the store at it.
  useEffect(() => {
    setActiveBusinessId(businessId);
  }, [businessId]);

  const unread = totalUnread(forBusiness(conversations.data ?? [], businessId));

  // The same AI Assistant item as the owner's. Its page here shows the package and the suggestions,
  // never a chat (`act-as-assistant.tsx`).
  const assistant = assistantNavState(business);
  const { forChildren } = useShape();
  const groups = useMemo<ShellNavGroup[]>(
    () => ownerNavGroups({ label, unread, teamPhoto: false, assistant, forChildren }),
    [label, unread, assistant, forChildren],
  );

  // The same Settings item as Desk: it stays lit on Get paid, which opens from Settings here too.
  // Act-as has no Teamna plan page, so that alias never matches.
  const bottom = useMemo<ShellNavItem[]>(() => [ownerSettingsItem()], []);

  const stop = () => {
    setActingAs(null);
    setActiveBusinessId(null);
    // Nothing read as the owner may paint on the next screen.
    queryClient.clear();
    void navigate({ to: "/businesses" });
  };

  return (
    <AppShell
      brand="Control Center"
      business={{ active: business, options: business ? [business] : [], onSelect: () => {} }}
      groups={groups}
      bottom={bottom}
      user={{ name: staff.name ?? staff.email, detail: staff.email }}
      signOut={signOut}
      sidebarTone="dark"
      banner={<ActAsBanner businessName={business?.name ?? businessName} onStop={stop} />}
    >
      <Outlet />
    </AppShell>
  );
}
